// ADR 16.9.26r §2 — the entry: the phones' FAB (`TutorFab.kt`). ADR 28.9.26e
// §9 narrowed it to Android's two routes and Android's `School` glyph.
//
// `/plans/:id` and `/vocab` — `TUTOR_FAB_ROUTES` exactly. Not `/flashcards`
// or `/flashcards/review` any more: Android's pager and review carry no FAB,
// and review is immersive now (no nav). Those two still publish tutor context
// for the pop-out. Not `/plans/:id/play`: the phones' pager has no FAB and
// nothing on it publishes. Not `/tutor` itself, and not `/s/:token` —
// strangers are not entitled and the fold says `hidden`.
//
// Mounted **once by `AdaptiveFrame`** as the last child of its outer column,
// appended after the three that exist so no index moves and the `<Outlet>`
// invariant holds; it returns `null` off-route. Three states from
// `tutorFabState` **and `emailVerified`**: `/vocab` sits outside
// `RequireVerified` where `/tutor` sits inside it, so without the conjunct a
// signed-in unverified learner would get a dock whose every send is a 403
// counted as `tutor_gate_drift`. Not rendered while the dock is `open` (its
// own ✕ is the way back); rendered with a "return" meaning while `detached`.

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuthSession } from '../../../auth/session'
import { useWindowClassValue } from '../../../layout/windowClassContext'
import { IconButton } from '../../../ui/controls'
import { showSnackbar } from '../../../ui/feedback'
import { snackbarState } from '../../../ui/feedback/snackbarStore'
import type { NavChrome, TutorFabState } from '../../../wasm'
import { UPGRADE_PATH } from '../../upgrade/preselect'
import { tutorFabState } from './fabState'
import { fabScreen } from './dockState'
import { lockedFabTapped, lockedFabUpgradeTaken } from './lockedFabAnalytics'
import { useTutorDock } from './TutorDockProvider'

/**
 * Above the bottom nav in offset, not only in z-order: the nav is `pt-3` +
 * an `h-8` glyph + `gap-1` + a `text-xs` label + `pb-4`, so it stays
 * reachable with the tutor open. Shared with the dock's sheet.
 */
export function bottomOffset(navChrome: NavChrome): string {
  return navChrome === 'bottom_bar' ? 'bottom-[calc(5rem+env(safe-area-inset-bottom))]' : 'bottom-4'
}

/** The corner, above the nav; `IconButton` owns the 44px floor and the ring. */
const FAB_PLACEMENT = 'fixed end-4 z-20 shadow-lg'

/**
 * ADR 8.10.26y §5 — a free learner's FAB explains itself and stays put: a tap
 * shows a snackbar whose action opens the plan picker. The id is kept so a
 * second tap while that entry is showing or waiting does nothing — no stack,
 * and nobody else's entry is reordered (a call-site dedupe, not *replace*).
 */
function LockedFab({ className }: { className: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const shown = useRef<number | null>(null)
  const onTap = () => {
    // ADR 8.10.26z §3 — before the dedupe: the module latch decides the emit.
    lockedFabTapped()
    const { current, pending } = snackbarState()
    const id = shown.current
    if (id !== null && (current?.id === id || pending.some((entry) => entry.id === id))) return
    const perform = () => {
      lockedFabUpgradeTaken()
      navigate(UPGRADE_PATH)
    }
    shown.current = showSnackbar({
      message: t('tutor_locked_snackbar'),
      action: { label: t('limit_upgrade_button'), perform },
    })
  }
  return (
    <IconButton
      icon="school"
      variant="ghost"
      label={t('tutor_fab_locked_label')}
      data-testid="tutor-fab-locked"
      className={`${FAB_PLACEMENT} ${className} bg-surface-sunken`}
      onClick={onTap}
    />
  )
}

/** The fold, with the `emailVerified` conjunct applied as a fourth input. */
function useFabState(): TutorFabState {
  const { status, flags, emailVerified } = useAuthSession()
  if (!emailVerified) return 'hidden'
  return tutorFabState(
    status === 'signedIn',
    flags?.has_paid_access ?? false,
    flags?.enable_ai_tutor ?? false,
  )
}

export function TutorFab() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { navChrome } = useWindowClassValue()
  const dock = useTutorDock()
  const button = useRef<HTMLButtonElement>(null)
  const screen = fabScreen(pathname)
  const state = useFabState()
  // The dock's ✕ hands focus here through the provider, since its own ref to
  // this button is null while the dock is open and the FAB unmounted.
  const takeFabFocus = dock.takeFabFocus
  useEffect(() => {
    if (dock.state !== 'open' && takeFabFocus()) button.current?.focus()
  }, [dock.state, takeFabFocus])

  if (screen === null || state === 'hidden' || dock.state === 'open') return null
  const offset = bottomOffset(navChrome)
  if (state === 'locked_upgrade') return <LockedFab className={offset} />
  return (
    <IconButton
      ref={button}
      icon="school"
      variant="tonal"
      label={t('tutor_fab_label')}
      data-testid="tutor-fab"
      data-dock-state={dock.state}
      className={`${FAB_PLACEMENT} ${offset}`}
      onClick={dock.state === 'detached' ? dock.returnToPopout : dock.open}
    />
  )
}
